// src/components/OutsourceUpdateModal.jsx
import React from 'react';
import { Modal, Button, Badge, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const OutsourceUpdateModal = ({ show, onHide, updates, userLabId }) => {
  const navigate = useNavigate();

  const handleCaseClick = (caseNumber, type) => {
    onHide();
    if (type === 'toOutsource') {
      navigate('/toOutsource');
    } else if (type === 'received') {
      navigate('/outsource-received');
    }
    // Scroll to specific case if needed
    setTimeout(() => {
      const element = document.getElementById(`case-${caseNumber}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 500);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'warning', text: 'Pending' },
      completed: { bg: 'success', text: 'Completed' },
      'in-progress': { bg: 'info', text: 'In Progress' },
      cancelled: { bg: 'danger', text: 'Cancelled' }
    };
    const config = statusConfig[status] || { bg: 'secondary', text: status };
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

return (
  <Modal
    show={show}
    onHide={onHide}
    size="lg"
    centered
  >
    <Modal.Header
      closeButton
      style={{
        background: "#004aad",
        color: "#fff",
        borderBottom: "none",
      }}
    >
      <Modal.Title
        style={{
          fontWeight: 600,
          fontSize: "20px",
        }}
      >
        <i className="fas fa-bell me-2"></i>
        Outsource Case Updates
      </Modal.Title>
    </Modal.Header>

    <Modal.Body
      style={{
        background: "#f8f9fa",
        padding: "20px",
      }}
    >
      {(!updates.toOutsource || updates.toOutsource.length === 0) &&
      (!updates.received || updates.received.length === 0) ? (
        <div className="text-center py-5">
          <i
            className="fas fa-check-circle"
            style={{
              fontSize: "60px",
              color: "#004aad",
            }}
          ></i>

          <h5
            className="mt-3"
            style={{
              color: "#004aad",
              fontWeight: 600,
            }}
          >
            You're All Caught Up
          </h5>

          <p className="text-muted mb-0">
            No new updates for your outsource cases.
          </p>
        </div>
      ) : (
        <>
          {updates.toOutsource && updates.toOutsource.length > 0 && (
            <div className="mb-4">
              <div
                className="d-flex align-items-center mb-3"
                style={{
                  borderLeft: "4px solid #004aad",
                  paddingLeft: "12px",
                }}
              >
                <h6
                  className="mb-0"
                  style={{
                    color: "#004aad",
                    fontWeight: 600,
                  }}
                >
                  <i className="fas fa-paper-plane me-2"></i>
                  Cases You Sent
                </h6>

                <Badge
                  className="ms-2"
                  style={{
                    background: "#004aad",
                  }}
                >
                  {updates.toOutsource.length}
                </Badge>
              </div>

              <ListGroup variant="flush">
                {updates.toOutsource.map((update) => (
                  <ListGroup.Item
                    key={update._id}
                    action
                    onClick={() =>
                      handleCaseClick(update.caseNumber, "toOutsource")
                    }
                    style={{
                      borderRadius: "12px",
                      marginBottom: "10px",
                      border: "1px solid #e5e7eb",
                      background: "#fff",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      padding: "15px",
                    }}
                    className="d-flex justify-content-between align-items-start"
                  >
                    <div className="me-auto">
                      <div
                        className="fw-bold mb-1"
                        style={{
                          color: "#004aad",
                        }}
                      >
                        Case #{update.caseNumber}
                        <span className="ms-2">
                          {getStatusBadge(update.status)}
                        </span>
                      </div>

                      <div className="text-muted small">
                        Procedure: {update.procedureName || "N/A"}
                      </div>

                      <div className="text-muted small mb-2">
                        To: {update.destinationLab?.labName || "Unknown Lab"}
                      </div>

                      <div>
                        {update.changes.hasNewNotes && (
                          <Badge
                            bg="light"
                            text="dark"
                            className="me-1 border"
                          >
                            <i className="fas fa-comment-alt me-1"></i>
                            New Notes
                          </Badge>
                        )}

                        {update.changes.hasNewFiles && (
                          <Badge
                            bg="light"
                            text="dark"
                            className="me-1 border"
                          >
                            <i className="fas fa-file me-1"></i>
                            New Files
                          </Badge>
                        )}

                        {update.changes.statusChanged && (
                          <Badge
                            style={{
                              background: "#004aad",
                            }}
                          >
                            <i className="fas fa-sync-alt me-1"></i>
                            Status Updated
                          </Badge>
                        )}
                      </div>
                    </div>

                    <small
                      style={{
                        color: "#6c757d",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDate(update.updatedAt)}
                    </small>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          {updates.received && updates.received.length > 0 && (
            <div>
              <div
                className="d-flex align-items-center mb-3"
                style={{
                  borderLeft: "4px solid #004aad",
                  paddingLeft: "12px",
                }}
              >
                <h6
                  className="mb-0"
                  style={{
                    color: "#004aad",
                    fontWeight: 600,
                  }}
                >
                  <i className="fas fa-inbox me-2"></i>
                  Cases You Received
                </h6>

                <Badge
                  className="ms-2"
                  style={{
                    background: "#004aad",
                  }}
                >
                  {updates.received.length}
                </Badge>
              </div>

              <ListGroup variant="flush">
                {updates.received.map((update) => (
                  <ListGroup.Item
                    key={update._id}
                    action
                    onClick={() =>
                      handleCaseClick(update.caseNumber, "received")
                    }
                    style={{
                      borderRadius: "12px",
                      marginBottom: "10px",
                      border: "1px solid #e5e7eb",
                      background: "#fff",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      padding: "15px",
                    }}
                    className="d-flex justify-content-between align-items-start"
                  >
                    <div className="me-auto">
                      <div
                        className="fw-bold mb-1"
                        style={{
                          color: "#004aad",
                        }}
                      >
                        Case #{update.caseNumber}
                        <span className="ms-2">
                          {getStatusBadge(update.status)}
                        </span>
                      </div>

                      <div className="text-muted small">
                        Procedure: {update.procedureName || "N/A"}
                      </div>

                      <div className="text-muted small mb-2">
                        From: {update.sourceLab?.labName || "Unknown Lab"}
                      </div>

                      <div>
                        {update.changes.hasNewNotes && (
                          <Badge
                            bg="light"
                            text="dark"
                            className="me-1 border"
                          >
                            <i className="fas fa-comment-alt me-1"></i>
                            New Notes
                          </Badge>
                        )}

                        {update.changes.hasNewFiles && (
                          <Badge
                            bg="light"
                            text="dark"
                            className="me-1 border"
                          >
                            <i className="fas fa-file me-1"></i>
                            New Files
                          </Badge>
                        )}

                        {update.changes.statusChanged && (
                          <Badge
                            style={{
                              background: "#004aad",
                            }}
                          >
                            <i className="fas fa-sync-alt me-1"></i>
                            Status Updated
                          </Badge>
                        )}
                      </div>
                    </div>

                    <small
                      style={{
                        color: "#6c757d",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDate(update.updatedAt)}
                    </small>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
        </>
      )}
    </Modal.Body>

    <Modal.Footer
      style={{
        background: "#fff",
        borderTop: "1px solid #e5e7eb",
      }}
    >
      <Button variant="light" onClick={onHide}>
        Close
      </Button>

      <Button
        style={{
          background: "#004aad",
          borderColor: "#004aad",
        }}
        onClick={() => {
          onHide();
          navigate("/toOutsource");
        }}
      >
        To Outsource
      </Button>

      <Button
        style={{
          background: "#004aad",
          borderColor: "#004aad",
        }}
        onClick={() => {
          onHide();
          navigate("/outsource-received");
        }}
      >
        Received Cases
      </Button>
    </Modal.Footer>
  </Modal>
);
};

export default OutsourceUpdateModal;